import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import AppButton from "./AppButton";
import AppInput from "./AppInput";
import { IconChevronDown, IconChevronLeft, IconChevronRight } from "../icons";

const MENU_OFFSET = 8;
const MENU_MAX_HEIGHT = 280;
const MENU_VIEWPORT_GUTTER = 16;

function normalizeItem(item) {
  if (typeof item === "string" || typeof item === "number") {
    return {
      value: item,
      label: String(item),
      disabled: false,
    };
  }

  return {
    value: item?.value,
    label: item?.label ?? String(item?.value ?? ""),
    disabled: Boolean(item?.disabled),
    meta: item,
  };
}

function filterItems(items, query) {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return items;
  }

  return items.filter((item) => item.label.toLowerCase().includes(normalizedQuery));
}

export default function AppNavigator({
  items = [],
  value,
  defaultValue = "",
  onChange,
  size = "md",
  searchPlaceholder = "Suchen...",
  emptyMessage = "Keine Einträge gefunden",
  previousAriaLabel = "Vorheriges Element",
  nextAriaLabel = "Nächstes Element",
  disabled = false,
  className = "",
  style,
  name,
}) {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [menuStyle, setMenuStyle] = useState(null);
  const menuId = useId();
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuAlignedRef = useRef(false);

  const normalizedItems = useMemo(() => items.map(normalizeItem), [items]);
  const enabledItems = useMemo(
    () => normalizedItems.filter((item) => !item.disabled),
    [normalizedItems]
  );
  const currentValue = isControlled ? value : internalValue;
  const selectedItem = useMemo(
    () => normalizedItems.find((item) => item.value === currentValue) ?? null,
    [currentValue, normalizedItems]
  );
  const activeItem = selectedItem ?? enabledItems[0] ?? null;
  const selectedIndex = useMemo(
    () => enabledItems.findIndex((item) => item.value === activeItem?.value),
    [activeItem, enabledItems]
  );
  const previousItem = selectedIndex > 0 ? enabledItems[selectedIndex - 1] : null;
  const nextItem =
    selectedIndex >= 0 && selectedIndex < enabledItems.length - 1
      ? enabledItems[selectedIndex + 1]
      : null;
  const filteredItems = useMemo(
    () => filterItems(normalizedItems, searchQuery),
    [normalizedItems, searchQuery]
  );

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setMenuStyle(null);
      return undefined;
    }

    function handlePointerDown(event) {
      const target = event.target;

      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }

      setOpen(false);
    }

    function handleEscape(event) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
    }
  }, [disabled]);

  useLayoutEffect(() => {
    if (!open || typeof window === "undefined") {
      return undefined;
    }

    function updateMenuPosition() {
      if (!rootRef.current || !triggerRef.current || !menuRef.current) {
        return;
      }

      const anchorRect = rootRef.current.getBoundingClientRect();
      const menuRect = menuRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const availableBelow = Math.max(
        0,
        viewportHeight - anchorRect.bottom - MENU_OFFSET - MENU_VIEWPORT_GUTTER
      );
      const availableAbove = Math.max(
        0,
        anchorRect.top - MENU_OFFSET - MENU_VIEWPORT_GUTTER
      );
      const naturalHeight = menuRect.height || MENU_MAX_HEIGHT;
      const preferredHeight = Math.min(naturalHeight, MENU_MAX_HEIGHT);
      const openAbove = availableBelow < preferredHeight && availableAbove > availableBelow;
      const maxHeight = Math.min(
        MENU_MAX_HEIGHT,
        openAbove ? availableAbove : availableBelow
      );
      const renderedHeight = Math.min(preferredHeight, maxHeight || MENU_MAX_HEIGHT);
      const width = Math.min(
        anchorRect.width,
        viewportWidth - MENU_VIEWPORT_GUTTER * 2
      );
      const left = Math.min(
        Math.max(MENU_VIEWPORT_GUTTER, anchorRect.left),
        viewportWidth - MENU_VIEWPORT_GUTTER - width
      );
      const top = openAbove
        ? Math.max(
            MENU_VIEWPORT_GUTTER,
            anchorRect.top - MENU_OFFSET - renderedHeight
          )
        : anchorRect.bottom + MENU_OFFSET;

      setMenuStyle({
        top,
        left,
        width,
        maxHeight: maxHeight || MENU_MAX_HEIGHT,
        visibility: "visible",
      });
    }

    updateMenuPosition();

    const animationFrameId = window.requestAnimationFrame(updateMenuPosition);
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => updateMenuPosition())
        : null;

    if (resizeObserver) {
      if (rootRef.current) {
        resizeObserver.observe(rootRef.current);
      }

      if (triggerRef.current) {
        resizeObserver.observe(triggerRef.current);
      }

      if (menuRef.current) {
        resizeObserver.observe(menuRef.current);
      }
    }

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
      resizeObserver?.disconnect();
    };
  }, [filteredItems, open, searchQuery]);

  useLayoutEffect(() => {
    if (!open) {
      menuAlignedRef.current = false;
      return undefined;
    }

    if (menuAlignedRef.current || searchQuery || !menuStyle) {
      return undefined;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      const menuElement = menuRef.current;
      const selectedOption = menuElement?.querySelector(".ds-navigator-menu__option--selected");

      if (!menuElement || !selectedOption) {
        return;
      }

      const searchElement = menuElement.querySelector(".ds-navigator-menu__search");
      const searchHeight = searchElement?.offsetHeight ?? 0;
      const availableHeight = Math.max(0, menuElement.clientHeight - searchHeight);
      const centeredScrollTop =
        selectedOption.offsetTop -
        searchHeight -
        Math.max(0, (availableHeight - selectedOption.offsetHeight) / 2);

      menuElement.scrollTop = Math.max(0, centeredScrollTop);
      menuAlignedRef.current = true;
    });

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [activeItem?.value, menuStyle, open, searchQuery]);

  function createChangeEvent(nextItem) {
    const target = {
      value: nextItem?.value ?? "",
      selection: nextItem,
      name,
    };

    return {
      type: "change",
      target,
      currentTarget: target,
    };
  }

  function updateValue(nextItem) {
    if (!isControlled) {
      setInternalValue(nextItem?.value ?? "");
    }

    if (onChange) {
      onChange(createChangeEvent(nextItem));
    }
  }

  function handleSelect(nextItem) {
    updateValue(nextItem);
    setOpen(false);
  }

  function handleStep(nextItem) {
    if (!nextItem || disabled) {
      return;
    }

    updateValue(nextItem);
    setOpen(false);
  }

  const classes = [
    "ds-navigator",
    size === "sm" ? "ds-navigator--sm" : "",
    size === "md" ? "ds-navigator--md" : "",
    open ? "ds-navigator--open" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const menu =
    open && !disabled ? (
      <div
        ref={menuRef}
        className="ds-navigator-menu"
        id={menuId}
        role="listbox"
        style={menuStyle ?? { visibility: "hidden" }}
      >
        <div className="ds-navigator-menu__search" onClick={(event) => event.stopPropagation()}>
          <AppInput
            size="sm"
            type="search"
            clearable
            autoFocus
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(event) => setSearchQuery(String(event.target?.value ?? ""))}
          />
        </div>

        {filteredItems.length > 0 ? (
          filteredItems.map((item) => {
            const isSelected = activeItem?.value === item.value;
            const optionClasses = [
              "ds-navigator-menu__option",
              isSelected ? "ds-navigator-menu__option--selected" : "",
              item.disabled ? "ds-navigator-menu__option--disabled" : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <button
                key={String(item.value)}
                type="button"
                className={optionClasses}
                onClick={() => handleSelect(item)}
                role="option"
                aria-selected={isSelected ? "true" : "false"}
                disabled={item.disabled}
              >
                <span className="ds-navigator-menu__option-label">{item.label}</span>
              </button>
            );
          })
        ) : (
          <div className="ds-navigator-menu__empty">{emptyMessage}</div>
        )}
      </div>
    ) : null;

  return (
    <>
      <div className={classes} style={style} ref={rootRef}>
        <AppButton
          variant="muted"
          size={size}
          iconOnly
          aria-label={previousAriaLabel}
          onClick={() => handleStep(previousItem)}
          disabled={disabled || !previousItem}
        >
          <IconChevronLeft sx={{ fontSize: 18 }} />
        </AppButton>

        <button
          ref={triggerRef}
          type="button"
          className="ds-navigator__trigger"
          aria-haspopup="listbox"
          aria-expanded={open ? "true" : "false"}
          aria-controls={open ? menuId : undefined}
          onClick={() => {
            if (!disabled) {
              setOpen((current) => !current);
            }
          }}
          disabled={disabled}
        >
          <span
            className="ds-navigator__value"
          >
            {activeItem?.label ?? ""}
          </span>
          <IconChevronDown className="ds-navigator__icon" sx={{ fontSize: 18 }} />
        </button>

        <AppButton
          variant="muted"
          size={size}
          iconOnly
          aria-label={nextAriaLabel}
          onClick={() => handleStep(nextItem)}
          disabled={disabled || !nextItem}
        >
          <IconChevronRight sx={{ fontSize: 18 }} />
        </AppButton>
      </div>

      {menu && typeof document !== "undefined" ? createPortal(menu, document.body) : menu}
    </>
  );
}
